
/**
 * Enhanced encryption/decryption utility for protecting API keys in localStorage
 * 
 * This implementation uses the Web Crypto API for better security where available,
 * with fallback to a simpler XOR encryption for older browsers.
 * 
 * NOTE: This is still frontend-only encryption and not meant for high-security needs.
 * For a production app, server-side storage with proper authentication would be preferred.
 */

// A simple encryption key - in a real app, this would be managed more securely
const ENCRYPTION_KEY = 'seo-analyzer-encryption-key-v2';
const SALT = 'seo-analyzer-salt-value-v2';

// Check if Web Crypto API is available
const hasCryptoSupport = typeof window !== 'undefined' && 
                          window.crypto && 
                          window.crypto.subtle && 
                          window.crypto.getRandomValues;

/**
 * Derive a key from the encryption key string
 * This uses PBKDF2 to generate a proper crypto key from our string
 */
async function deriveKey(): Promise<CryptoKey> {
  try {
    // Convert string to ArrayBuffer
    const encoder = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(ENCRYPTION_KEY),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    );
    
    // Derive a key using PBKDF2
    return window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode(SALT),
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  } catch (error) {
    console.error('Error deriving crypto key:', error);
    throw error;
  }
}

/**
 * Modern encryption using Web Crypto API
 */
async function modernEncrypt(data: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const key = await deriveKey();
    
    // Create initialization vector
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    
    // Encrypt the data
    const encryptedData = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv
      },
      key,
      encoder.encode(data)
    );
    
    // Combine IV and encrypted data and convert to base64
    const combinedData = new Uint8Array(iv.length + new Uint8Array(encryptedData).length);
    combinedData.set(iv);
    combinedData.set(new Uint8Array(encryptedData), iv.length);
    
    return btoa(String.fromCharCode.apply(null, Array.from(combinedData)));
  } catch (error) {
    console.error('Error encrypting data with modern method:', error);
    // Fall back to simple encryption
    return simpleEncrypt(data);
  }
}

/**
 * Modern decryption using Web Crypto API
 */
async function modernDecrypt(encryptedData: string): Promise<string> {
  try {
    // Convert from base64
    const binaryString = atob(encryptedData);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Extract IV and data
    const iv = bytes.slice(0, 12);
    const data = bytes.slice(12);
    
    // Decrypt
    const key = await deriveKey();
    const decryptedData = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv
      },
      key,
      data
    );
    
    // Convert back to string
    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  } catch (error) {
    console.error('Error decrypting data with modern method, will try simple method', error);
    // Try simple decryption if modern fails
    return simpleDecrypt(encryptedData);
  }
}

/**
 * Simple encryption using XOR (fallback for browsers without Crypto API)
 * This version is improved with a version marker to help with decryption
 */
function simpleEncrypt(data: string): string {
  if (!data) return '';
  
  // Add a version marker to the beginning of the data
  const markedData = 'v2:' + data;
  
  const result = [];
  for (let i = 0; i < markedData.length; i++) {
    const charCode = markedData.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
    result.push(String.fromCharCode(charCode));
  }
  
  return btoa(result.join(''));
}

/**
 * Simple decryption using XOR (fallback for browsers without Crypto API)
 */
function simpleDecrypt(encryptedData: string): string {
  if (!encryptedData) return '';
  
  try {
    const data = atob(encryptedData);
    const result = [];
    
    for (let i = 0; i < data.length; i++) {
      const charCode = data.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
      result.push(String.fromCharCode(charCode));
    }
    
    const decrypted = result.join('');
    
    // Check for version marker and remove it
    if (decrypted.startsWith('v2:')) {
      return decrypted.substring(3);
    }
    
    return decrypted;
  } catch (error) {
    console.error('Error decrypting data with simple method', error);
    return '';
  }
}

/**
 * Encrypts data using the best available method
 * @param data The string data to encrypt
 * @returns Encrypted string in base64
 */
export async function encryptData(data: string): Promise<string> {
  if (!data) return '';
  
  try {
    if (hasCryptoSupport) {
      return await modernEncrypt(data);
    } else {
      return simpleEncrypt(data);
    }
  } catch (error) {
    console.error('Encryption failed, using simple encryption as fallback', error);
    return simpleEncrypt(data);
  }
}

/**
 * Synchronous version of encryptData - uses simple encryption
 * This is maintained for backward compatibility with existing code
 * @param data The string data to encrypt
 * @returns Encrypted string in base64
 */
export function encryptDataSync(data: string): string {
  return simpleEncrypt(data);
}

/**
 * Decrypts data using the best available method
 * @param encryptedData The encrypted string in base64
 * @returns Decrypted string
 */
export async function decryptData(encryptedData: string): Promise<string> {
  if (!encryptedData) return '';
  
  try {
    if (hasCryptoSupport) {
      return await modernDecrypt(encryptedData);
    } else {
      return simpleDecrypt(encryptedData);
    }
  } catch (error) {
    console.error('Decryption failed, using simple decryption as fallback', error);
    return simpleDecrypt(encryptedData);
  }
}

/**
 * Synchronous version of decryptData - uses simple decryption
 * This is maintained for backward compatibility with existing code
 * @param encryptedData The encrypted string in base64
 * @returns Decrypted string
 */
export function decryptDataSync(encryptedData: string): string {
  try {
    return simpleDecrypt(encryptedData);
  } catch (error) {
    console.error('Sync decryption failed', error);
    return '';
  }
}

/**
 * Masks an API key for display, showing only the first few and last few characters
 * @param apiKey The API key to mask
 * @returns Masked API key string
 */
export function maskApiKey(apiKey: string): string {
  if (!apiKey) return '';
  if (apiKey.length < 8) return '••••••';
  
  const firstFour = apiKey.substring(0, 4);
  const lastFour = apiKey.substring(apiKey.length - 4);
  return `${firstFour}••••••••${lastFour}`;
}
